import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import { Search } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '@/components/Layout/PageHeader.jsx';
import StickyActions from '@/components/StickyActions.jsx';
import Field from '@/pages/Guias/components/Field.jsx';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { resolvePermissionPreview } from './permissionPreview.js';
import { isSensitivePermissionCode } from '@/security/catalog.js';
import {
  SECURITY_AUDIT_QUERY,
  SECURITY_USER_CREATE_QUERY,
  SECURITY_USER_DETAIL_QUERY,
  SECURITY_USER_SAVE_MUTATION,
} from './gql.js';

const EMPTY_ARRAY = Object.freeze([]);
const EMPTY_FORM = {
  email: '', username: '', nombre: '', apellido: '', password: '', activo: true,
  bloqueado: false, mustReset: false, observacionesInternas: '', nivel: 100,
  allowWithoutEmployee: false, empleadoId: '', roleIds: [], rolPrincipalId: '',
  permissionGrants: [], permissionDenies: [], sucursalIds: [], sucursalBaseId: '',
  sucursalActivaId: '', grantReason: '', denyReason: '',
};

function groupByModule(permissions = []) {
  return permissions.reduce((acc, permission) => {
    if (!acc[permission.module]) acc[permission.module] = [];
    acc[permission.module].push(permission);
    return acc;
  }, {});
}

function normalizeId(value) {
  return value === null || value === undefined ? '' : String(value);
}

function extractGraphQLErrorMessage(error) {
  if (!error) return '';
  const graphQLError = Array.isArray(error.graphQLErrors) ? error.graphQLErrors[0] : null;
  const detail = graphQLError?.extensions?.detail;
  const field = graphQLError?.extensions?.field;
  if (detail || field) {
    return `${field ? `${field}: ` : ''}${detail || graphQLError?.message || error.message || 'Error desconocido en GraphQL'}`;
  }
  return graphQLError?.message || error?.networkError?.message || error.message || 'Error desconocido en GraphQL';
}

export default function UsuarioSecurityForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { user: currentUser, hasLevelAtLeast } = useAuth();
  const [filter, setFilter] = useState('');
  const [activeTab, setActiveTab] = useState('personal');
  const [selectedModule, setSelectedModule] = useState('');
  const [permissionFilter, setPermissionFilter] = useState('all');
  const [showEffective, setShowEffective] = useState(false);
  const [effectiveModule, setEffectiveModule] = useState('all');
  const [selectedSucursalPreview, setSelectedSucursalPreview] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hydratedUserIdRef = useRef(null);

  const [{ data, error }] = useQuery({
    query: isEdit ? SECURITY_USER_DETAIL_QUERY : SECURITY_USER_CREATE_QUERY,
    variables: isEdit ? { id, effectiveSucursalId: selectedSucursalPreview } : {},
  });
  const [{ data: auditData }] = useQuery({
    query: SECURITY_AUDIT_QUERY,
    variables: { entityType: 'USER', entityId: id },
    pause: !isEdit,
  });
  const [, saveUser] = useMutation(SECURITY_USER_SAVE_MUTATION);

  const userDetail = data?.security_user;
  const roles = data?.security_roles || EMPTY_ARRAY;
  const catalog = data?.security_catalog?.permissions || EMPTY_ARRAY;
  const empleados = data?.security_empleados || EMPTY_ARRAY;
  const sucursales = data?.security_sucursales || EMPTY_ARRAY;

  const [form, setForm] = useState(EMPTY_FORM);
  const initialFormSnapshotRef = useRef(JSON.stringify(EMPTY_FORM));

  useEffect(() => {
    if (!userDetail) return;
    if (hydratedUserIdRef.current === String(userDetail.id)) return;

    hydratedUserIdRef.current = String(userDetail.id);
    setSelectedSucursalPreview(userDetail.sucursalActivaId || null);
    const nextForm = {
      email: userDetail.email || '',
      username: userDetail.username || '',
      nombre: userDetail.nombre || '',
      apellido: userDetail.apellido || '',
      password: '',
      activo: userDetail.activo ?? true,
      bloqueado: userDetail.bloqueado ?? false,
      mustReset: userDetail.mustReset ?? false,
      observacionesInternas: userDetail.observacionesInternas || '',
      nivel: userDetail.nivel || 100,
      allowWithoutEmployee: userDetail.allowWithoutEmployee ?? false,
      empleadoId: userDetail.empleadoId || '',
      roleIds: (userDetail.roles || []).map((role) => role.id),
      rolPrincipalId: userDetail.rolPrincipalId || userDetail.roles?.[0]?.id || '',
      permissionGrants: (userDetail.permissionGrants || []).map((permission) => permission.code),
      permissionDenies: (userDetail.permissionDenies || []).map((permission) => permission.code),
      sucursalIds: (userDetail.sucursalesHabilitadas || []).map((branch) => normalizeId(branch.id)),
      sucursalBaseId: normalizeId(userDetail.sucursalBaseId),
      sucursalActivaId: normalizeId(userDetail.sucursalActivaId),
      grantReason: '',
      denyReason: '',
    };
    initialFormSnapshotRef.current = JSON.stringify(nextForm);
    setForm(nextForm);
  // Cambiar la sucursal activa vuelve a consultar el preview de permisos. No
  // debemos rehidratar el formulario con esa respuesta porque reemplazaría la
  // selección que el administrador acaba de hacer.
  }, [userDetail]);

  const hasUnsavedChanges = initialFormSnapshotRef.current !== null
    && JSON.stringify(form) !== initialFormSnapshotRef.current;
  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [hasUnsavedChanges]);

  const specialPermissions = useMemo(() => catalog.filter((permission) => isSensitivePermissionCode(permission.code)), [catalog]);
  const canManageSpecialPermissions = Boolean(currentUser?.id)
    && Number(currentUser?.nivel || 0) >= 900
    && (!isEdit || String(currentUser.id) !== String(id));
  const permissionsByModule = useMemo(() => groupByModule(catalog.filter((permission) => !isSensitivePermissionCode(permission.code))), [catalog]);
  const moduleNames = useMemo(() => Object.keys(permissionsByModule).sort((a, b) => a.localeCompare(b)), [permissionsByModule]);
  const activeModule = selectedModule || moduleNames[0] || '';
  const permissionPreview = useMemo(() => resolvePermissionPreview({
    roles: roles.filter((role) => form.roleIds.includes(role.id)),
    grants: form.permissionGrants,
    denies: form.permissionDenies,
  }), [form.permissionDenies, form.permissionGrants, form.roleIds, roles]);
  const inheritedPermissions = useMemo(
    () => catalog.filter((permission) => permissionPreview.inherited.includes(permission.code)),
    [catalog, permissionPreview.inherited],
  );
  const effectivePermissions = useMemo(
    () => catalog.filter((permission) => permissionPreview.effective.includes(permission.code)),
    [catalog, permissionPreview.effective],
  );
  const visiblePermissions = useMemo(() => (permissionsByModule[activeModule] || []).filter((permission) => {
    const haystack = `${permission.code} ${permission.name} ${permission.description || ''} ${permission.module || ''}`.toLowerCase();
    if (filter && !haystack.includes(filter.toLowerCase())) return false;
    const inherited = inheritedPermissions.some((item) => item.code === permission.code);
    const granted = form.permissionGrants.includes(permission.code);
    const denied = form.permissionDenies.includes(permission.code);
    if (permissionFilter === 'inherited' && !inherited) return false;
    if (permissionFilter === 'granted' && !granted) return false;
    if (permissionFilter === 'denied' && !denied) return false;
    if (permissionFilter === 'custom' && !granted && !denied) return false;
    if (permissionFilter === 'effective' && !(granted || (inherited && !denied))) return false;
    return true;
  }), [activeModule, filter, form.permissionDenies, form.permissionGrants, inheritedPermissions, permissionFilter, permissionsByModule]);

  const effectivePreviewRows = useMemo(() => catalog.filter((permission) => {
    if (effectiveModule !== 'all' && permission.module !== effectiveModule) return false;
    const searchText = `${permission.code} ${permission.name} ${permission.description || ''} ${permission.module || ''}`.toLowerCase();
    return !filter || searchText.includes(filter.toLowerCase());
  }), [catalog, effectiveModule, filter]);
  const effectivePreviewGroups = useMemo(() => groupByModule(effectivePreviewRows), [effectivePreviewRows]);

  const selectedEmpleado = useMemo(
    () => empleados.find((empleado) => String(empleado.id) === String(form.empleadoId)),
    [empleados, form.empleadoId],
  );

  const hierarchyWarning = useMemo(() => {
    const maxRoleLevel = roles
      .filter((role) => form.roleIds.includes(role.id))
      .reduce((maxValue, role) => Math.max(maxValue, Number(role.nivel || 0)), 0);

    if (Number(form.nivel || 0) > Number(currentUser?.nivel || 0)) {
      return 'El nivel del usuario supera tu máximo administrable.';
    }
    if (maxRoleLevel > Number(currentUser?.nivel || 0)) {
      return 'Seleccionaste un rol con nivel por encima de tu jerarquía actual.';
    }
    return null;
  }, [currentUser?.nivel, form.nivel, form.roleIds, roles]);

  const toggleArrayValue = (field, value) => {
    const normalizedValue = normalizeId(value);
    setForm((current) => ({
      ...current,
      [field]: current[field].map(normalizeId).includes(normalizedValue)
        ? current[field].filter((item) => normalizeId(item) !== normalizedValue)
        : [...current[field], normalizedValue],
    }));
  };

  const selectedSucursalIds = useMemo(
    () => new Set(form.sucursalIds.map(normalizeId)),
    [form.sucursalIds],
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFeedback(null);

    if (!form.roleIds.length) {
      setFeedback({ type: 'error', message: 'Seleccioná al menos un rol antes de guardar.' });
      return;
    }
    if (!form.empleadoId && !form.allowWithoutEmployee) {
      setFeedback({ type: 'error', message: 'Vinculá un empleado o activá "Permitir sin empleado".' });
      return;
    }
    if (!form.sucursalBaseId) {
      setFeedback({ type: 'error', message: 'Seleccioná una sucursal base.' });
      return;
    }

    const sucursalIds = Array.from(
      new Set([
        ...form.sucursalIds.map(normalizeId),
        normalizeId(form.sucursalBaseId),
        normalizeId(form.sucursalActivaId),
      ].filter(Boolean)),
    );

    setIsSubmitting(true);
    const payload = {
      id,
      email: form.email,
      username: form.username || null,
      nombre: form.nombre,
      apellido: form.apellido,
      password: form.password || null,
      activo: form.activo,
      bloqueado: form.bloqueado,
      mustReset: form.mustReset,
      observacionesInternas: form.observacionesInternas || null,
      nivel: Number(form.nivel),
      allowWithoutEmployee: form.allowWithoutEmployee,
      empleadoId: form.empleadoId || null,
      roleIds: form.roleIds,
      rolPrincipalId: form.rolPrincipalId || form.roleIds[0] || null,
      permissionGrants: form.permissionGrants,
      permissionDenies: form.permissionDenies,
      sucursalIds,
      sucursalBaseId: form.sucursalBaseId || null,
      sucursalActivaId: form.sucursalActivaId || form.sucursalBaseId || null,
      grantReason: form.grantReason || null,
      denyReason: form.denyReason || null,
    };

    try {
      const response = await saveUser({ input: payload });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error);
        console.error('[Seguridad][Usuarios] Error al guardar usuario', {
          isEdit,
          payload,
          error: response.error,
        });
        setFeedback({ type: 'error', message: message || 'No se pudo guardar el usuario.' });
        return;
      }
      if (!response.data?.security_user_save?.id) {
        console.error('[Seguridad][Usuarios] Respuesta sin usuario guardado', {
          isEdit,
          payload,
          response,
        });
        setFeedback({ type: 'error', message: 'El backend no confirmó la creación del usuario.' });
        return;
      }
      navigate('/seguridad/usuarios');
    } catch (submitError) {
      console.error('[Seguridad][Usuarios] Falló el submit del usuario', {
        isEdit,
        payload,
        submitError,
      });
      setFeedback({
        type: 'error',
        message: submitError?.message || 'Ocurrió un error inesperado al guardar el usuario.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[rgb(var(--bg))] pb-20">
      <form onSubmit={handleSubmit} className="mx-auto flex w-full max-w-[1400px] flex-col gap-6 px-6 py-10">
        <PageHeader
          title={isEdit ? 'Editar usuario' : 'Nuevo usuario'}
          subtitle="Administrá identidad, roles y permisos desde una vista organizada."
        />
        <nav aria-label="Secciones de edición" className="flex flex-wrap gap-2 border-b border-[rgba(var(--border),0.5)]">
          {[['personal', 'Datos personales'], ['roles', 'Roles y seguridad'], ['permissions', 'Permisos']].map(([key, label]) => (
            <button key={key} type="button" onClick={() => setActiveTab(key)} aria-current={activeTab === key ? 'page' : undefined} className={`border-b-2 px-3 py-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] ${activeTab === key ? 'border-[rgb(var(--brand))] text-[rgb(var(--brand))]' : 'border-transparent text-[rgb(var(--muted))]'}`}>{label}</button>
          ))}
        </nav>

        {isEdit && userDetail ? <div className="-mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[rgb(var(--muted))]">
          <span className="font-semibold text-[rgb(var(--fg))]">{`${userDetail.nombre || ''} ${userDetail.apellido || ''}`.trim()}</span>
          <span>{userDetail.email}</span><span className="rounded-full border px-2 py-0.5 text-xs">{userDetail.activo ? 'Activo' : 'Inactivo'}</span>
          {roles.find((role) => String(role.id) === String(form.rolPrincipalId))?.nombre ? <span>Rol: {roles.find((role) => String(role.id) === String(form.rolPrincipalId)).nombre}</span> : null}
        </div> : null}
        {hasUnsavedChanges ? <p className="text-sm font-medium text-amber-700" role="status">Tenés cambios sin guardar.</p> : null}

        {hierarchyWarning ? (
          <div className="rounded-2xl border border-[rgba(245,158,11,0.45)] bg-[rgba(245,158,11,0.12)] px-4 py-3 text-sm text-amber-700 dark:text-amber-200">
            {hierarchyWarning}
          </div>
        ) : null}

        {error ? <div className="rounded-2xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">{error.message}</div> : null}
        {feedback ? (
          <div
            className={`rounded-2xl border px-4 py-3 text-sm ${
              feedback.type === 'error'
                ? 'border-red-400/60 bg-red-500/10 text-red-200'
                : 'border-emerald-400/60 bg-emerald-500/10 text-emerald-200'
            }`}
            role="alert"
          >
            {feedback.message}
          </div>
        ) : null}

        {activeTab === 'personal' ? <section className="card rounded-2xl p-6">
          <div className="grid grid-cols-12 gap-4">
            <Field label="Email" htmlFor="email" required className="col-span-12 md:col-span-4">
              <input id="email" type="email" required value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-2.5 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]" />
            </Field>
            <Field label="Usuario" htmlFor="username" className="col-span-12 md:col-span-3">
              <input id="username" value={form.username} onChange={(event) => setForm((current) => ({ ...current, username: event.target.value }))} className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-2.5 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]" />
            </Field>
            <Field label="Nombre" htmlFor="nombre" required className="col-span-12 md:col-span-2">
              <input id="nombre" required value={form.nombre} onChange={(event) => setForm((current) => ({ ...current, nombre: event.target.value }))} className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-2.5 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]" />
            </Field>
            <Field label="Apellido" htmlFor="apellido" required className="col-span-12 md:col-span-3">
              <input id="apellido" required value={form.apellido} onChange={(event) => setForm((current) => ({ ...current, apellido: event.target.value }))} className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-2.5 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]" />
            </Field>
            <Field label={isEdit ? 'Reset de contraseña' : 'Contraseña'} htmlFor="password" className="col-span-12 md:col-span-3">
              <input id="password" type="password" value={form.password} onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))} className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-2.5 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]" />
            </Field>
            <Field label="Nivel" htmlFor="nivel" required className="col-span-12 md:col-span-2">
              <input id="nivel" type="number" min="0" value={form.nivel} onChange={(event) => setForm((current) => ({ ...current, nivel: Number(event.target.value) }))} className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-2.5 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]" />
            </Field>
            <Field label="Empleado vinculado" htmlFor="empleadoId" className="col-span-12 md:col-span-4">
              <select id="empleadoId" value={form.empleadoId} onChange={(event) => setForm((current) => ({ ...current, empleadoId: event.target.value }))} className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-2.5 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]">
                <option value="">Sin empleado</option>
                {empleados.map((empleado) => (
                  <option key={empleado.id} value={empleado.id}>
                    {empleado.legajo || 'Sin legajo'} · {empleado.apellido}, {empleado.nombre}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Observaciones internas" htmlFor="observaciones" className="col-span-12 md:col-span-3">
              <input id="observaciones" value={form.observacionesInternas} onChange={(event) => setForm((current) => ({ ...current, observacionesInternas: event.target.value }))} className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-2.5 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]" />
            </Field>
          </div>

          <div className="mt-5 flex flex-wrap gap-4 text-sm text-[rgb(var(--fg))]">
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.activo} onChange={(event) => setForm((current) => ({ ...current, activo: event.target.checked }))} className="h-4 w-4 rounded" /> Activo</label>
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.bloqueado} onChange={(event) => setForm((current) => ({ ...current, bloqueado: event.target.checked }))} className="h-4 w-4 rounded" /> Bloqueado</label>
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.mustReset} onChange={(event) => setForm((current) => ({ ...current, mustReset: event.target.checked }))} className="h-4 w-4 rounded" /> Forzar reset</label>
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.allowWithoutEmployee} onChange={(event) => setForm((current) => ({ ...current, allowWithoutEmployee: event.target.checked }))} className="h-4 w-4 rounded" /> Permitir sin empleado</label>
          </div>

          {selectedEmpleado ? (
            <div className="mt-4 rounded-2xl border border-[rgba(var(--border),0.4)] bg-[rgba(var(--subpanel),0.6)] px-4 py-3 text-sm text-[rgba(var(--fg),0.8)]">
              Legajo: {selectedEmpleado.legajo || 'Sin legajo'} · DNI: {selectedEmpleado.dni || 'Sin DNI'} · Email: {selectedEmpleado.email || 'Sin email'} · Puesto: {selectedEmpleado.puesto || 'Sin puesto'}
            </div>
          ) : null}
        </section> : null}

        {activeTab === 'roles' ? <section className="grid gap-4 xl:grid-cols-2">
          <div className="card rounded-2xl p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2"><div><h2 className="text-lg font-semibold">Roles y seguridad</h2><p className="text-sm text-[rgb(var(--muted))]">{inheritedPermissions.length} permisos proporcionados por los roles seleccionados</p></div><button type="button" onClick={() => { setSelectedModule(roles.find((role) => form.roleIds.includes(role.id))?.permisos?.[0]?.module || moduleNames[0] || ''); setActiveTab('permissions'); }} className="rounded-lg border px-3 py-2 text-sm font-semibold">Ver permisos del rol</button></div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-[rgba(var(--border),0.4)] bg-[rgba(var(--panel),0.78)] p-4">
                <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-[rgb(var(--muted))]">Roles</h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  {roles.map((role) => (
                    <button
                      key={role.id}
                      type="button"
                      onClick={() => toggleArrayValue('roleIds', role.id)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] ${form.roleIds.includes(role.id) ? 'border-[rgba(var(--brand),0.45)] bg-[rgba(var(--brand),0.12)] text-[rgb(var(--brand))]' : 'border-[rgba(var(--border),0.65)] text-[rgb(var(--fg))]'}`}
                    >
                      {role.nombre} · N{role.nivel}
                    </button>
                  ))}
                </div>
                <Field label="Rol principal" htmlFor="rolPrincipalId" className="mt-4">
                  <select id="rolPrincipalId" value={form.rolPrincipalId} onChange={(event) => setForm((current) => ({ ...current, rolPrincipalId: event.target.value }))} className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-2.5 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]">
                    <option value="">Seleccionar</option>
                    {roles.filter((role) => form.roleIds.includes(role.id)).map((role) => <option key={role.id} value={role.id}>{role.nombre}</option>)}
                  </select>
                </Field>
              </div>

              <div className="rounded-2xl border border-[rgba(var(--border),0.4)] bg-[rgba(var(--panel),0.78)] p-4">
                <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-[rgb(var(--muted))]">Sucursales</h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  {sucursales.map((branch) => (
                    <button
                      key={branch.id}
                      type="button"
                      onClick={() => toggleArrayValue('sucursalIds', branch.id)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] ${selectedSucursalIds.has(normalizeId(branch.id)) ? 'border-[rgba(var(--brand),0.45)] bg-[rgba(var(--brand),0.12)] text-[rgb(var(--brand))]' : 'border-[rgba(var(--border),0.65)] text-[rgb(var(--fg))]'}`}
                    >
                      {branch.nombre}
                    </button>
                  ))}
                </div>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <Field label="Sucursal base" htmlFor="sucursalBaseId">
                    <select id="sucursalBaseId" value={form.sucursalBaseId} onChange={(event) => setForm((current) => ({ ...current, sucursalBaseId: event.target.value }))} className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-2.5 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]">
                      <option value="">Seleccionar</option>
                      {sucursales.map((branch) => <option key={branch.id} value={branch.id}>{branch.nombre}</option>)}
                    </select>
                  </Field>
                  <Field label="Sucursal activa" htmlFor="sucursalActivaId">
                    <select id="sucursalActivaId" value={form.sucursalActivaId} onChange={(event) => { setSelectedSucursalPreview(event.target.value); setForm((current) => ({ ...current, sucursalActivaId: event.target.value })); }} className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-2.5 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]">
                      <option value="">Seleccionar</option>
                      {sucursales.map((branch) => <option key={branch.id} value={branch.id}>{branch.nombre}</option>)}
                    </select>
                  </Field>
                </div>
              </div>
            </div>

            {isEdit ? <details className="mt-4 rounded-xl border border-[rgba(var(--border),0.4)] p-3"><summary className="cursor-pointer text-sm font-semibold">Auditoría reciente</summary><div className="mt-3 divide-y">{(auditData?.security_audit || []).slice(0, 8).map((entry) => <article key={entry.id} className="py-2"><p className="text-sm font-medium">{entry.action}</p><p className="text-xs text-[rgb(var(--muted))]">{new Date(entry.createdAt).toLocaleString()}</p></article>)}</div></details> : null}

          </div>


        </section> : null}

        {activeTab === 'permissions' ? <section className="card rounded-2xl p-4" aria-labelledby="special-permissions-title">
          <h2 id="special-permissions-title" className="text-lg font-semibold">PERMISOS ESPECIALES</h2>
          <p className="mt-1 text-sm text-[rgb(var(--muted))]">Complementan los permisos normales de cada módulo. Los cambios requieren nivel 900 o superior y un motivo; no se pueden modificar los propios.</p>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            {specialPermissions.map((permission) => <label key={permission.code} className="flex items-start gap-3 rounded-xl border border-[rgba(var(--border),0.4)] p-3">
              <input type="checkbox" className="mt-1 h-4 w-4 shrink-0" disabled={!canManageSpecialPermissions}
                checked={permissionPreview.effective.includes(permission.code)}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setForm((current) => ({
                    ...current,
                    permissionGrants: [...current.permissionGrants.filter((code) => code !== permission.code), ...(checked ? [permission.code] : [])],
                    permissionDenies: [...current.permissionDenies.filter((code) => code !== permission.code), ...(checked ? [] : [permission.code])],
                  }));
                }} />
              <span><span className="block text-sm font-semibold">{permission.name}</span><span className="block text-xs text-[rgb(var(--muted))]">{permission.description}</span></span>
            </label>)}
          </div>
        </section> : null}

        {activeTab === 'permissions' ? <section className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
          <div className="card h-fit rounded-2xl p-3">
            <h2 className="px-2 py-2 text-sm font-semibold">Módulos</h2>
            <div className="max-h-[55vh] space-y-1 overflow-y-auto">
              {moduleNames.map((moduleName) => <button key={moduleName} type="button" onClick={() => setSelectedModule(moduleName)} className={`flex w-full items-center justify-between rounded-lg px-2 py-2 text-left text-sm ${activeModule === moduleName ? 'bg-[rgba(var(--brand),0.12)] font-semibold text-[rgb(var(--brand))]' : 'text-[rgb(var(--fg))]'}`}><span>{moduleName}</span><span className="text-xs text-[rgb(var(--muted))]">{permissionsByModule[moduleName].length}</span></button>)}
            </div>
          </div>
          <div className="card min-w-0 rounded-2xl p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3"><div><h2 className="text-lg font-semibold">Permisos</h2><p className="text-sm text-[rgb(var(--muted))]">{effectivePermissions.length} efectivos · {form.permissionGrants.length + form.permissionDenies.length} personalizados</p></div><button type="button" onClick={() => setShowEffective(true)} className="rounded-lg border px-3 py-2 text-sm font-semibold">Ver permisos efectivos</button></div>
            <div className="mb-3 flex flex-wrap gap-2">
              <label className="relative min-w-[220px] flex-1"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[rgb(var(--muted))]"/><input aria-label="Buscar permisos" value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Buscar permisos..." className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] py-2.5 pl-10 pr-3 text-sm"/></label>
              <select aria-label="Filtrar permisos" value={permissionFilter} onChange={(event) => setPermissionFilter(event.target.value)} className="rounded-xl border bg-[rgba(var(--panel),0.95)] px-3 py-2 text-sm"><option value="all">Todos los permisos</option><option value="inherited">Heredados</option><option value="granted">Concedidos manualmente</option><option value="denied">Denegados manualmente</option><option value="custom">Solo personalizaciones</option><option value="effective">Efectivos</option></select>
            </div>
            <div className="max-h-[58vh] overflow-y-auto divide-y divide-[rgba(var(--border),0.4)]">
              {visiblePermissions.map((permission) => {
                const inherited = inheritedPermissions.some((item) => item.code === permission.code);
                const granted = form.permissionGrants.includes(permission.code);
                const denied = form.permissionDenies.includes(permission.code);
                const status = permissionPreview.effective.includes(permission.code);
                return <div key={permission.code} className="flex flex-wrap items-center justify-between gap-3 py-3">
                  <div className="min-w-[180px] flex-1"><p className="text-sm font-semibold">{permission.name || permission.code}</p><p className="text-xs text-[rgb(var(--muted))]">{permission.description || ''}</p><code className="text-[11px] text-[rgb(var(--muted))]">{permission.code}</code><span className="ml-2 text-xs text-[rgb(var(--muted))]">{granted ? 'Concedido manualmente' : denied ? 'Denegado manualmente' : inherited ? 'Heredado por rol' : 'Sin permiso'} · {status ? 'Permitido' : 'No permitido'}</span></div>
                  <div className="flex shrink-0 gap-1" role="group" aria-label={`Personalización ${permission.name || permission.code}`}>
                    <button type="button" aria-pressed={!granted && !denied} onClick={() => setForm((current) => ({ ...current, permissionGrants: current.permissionGrants.filter((code) => code !== permission.code), permissionDenies: current.permissionDenies.filter((code) => code !== permission.code) }))} className="rounded-md border px-2 py-1 text-xs">Heredar</button>
                    <button type="button" aria-pressed={granted} onClick={() => setForm((current) => ({ ...current, permissionGrants: current.permissionGrants.includes(permission.code) ? current.permissionGrants : [...current.permissionGrants, permission.code], permissionDenies: current.permissionDenies.filter((code) => code !== permission.code) }))} className="rounded-md border px-2 py-1 text-xs">Permitir</button>
                    <button type="button" aria-pressed={denied} onClick={() => setForm((current) => ({ ...current, permissionDenies: current.permissionDenies.includes(permission.code) ? current.permissionDenies : [...current.permissionDenies, permission.code], permissionGrants: current.permissionGrants.filter((code) => code !== permission.code) }))} className="rounded-md border px-2 py-1 text-xs">Denegar</button>
                  </div>
                </div>;
              })}
              {!visiblePermissions.length ? <p className="py-8 text-center text-sm text-[rgb(var(--muted))]">No hay permisos que coincidan.</p> : null}
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <Field label="Motivo de Permitir crítico" htmlFor="grantReason"><textarea id="grantReason" rows="2" value={form.grantReason} onChange={(event) => setForm((current) => ({ ...current, grantReason: event.target.value }))} className="w-full rounded-xl border bg-[rgba(var(--panel),0.95)] px-3 py-2 text-sm" /></Field>
              <Field label="Motivo de Denegar crítico" htmlFor="denyReason"><textarea id="denyReason" rows="2" value={form.denyReason} onChange={(event) => setForm((current) => ({ ...current, denyReason: event.target.value }))} className="w-full rounded-xl border bg-[rgba(var(--panel),0.95)] px-3 py-2 text-sm" /></Field>
            </div>
          </div>
          {showEffective ? <div role="dialog" aria-modal="true" aria-label="Permisos efectivos" className="fixed inset-y-0 right-0 z-50 w-full max-w-lg overflow-y-auto border-l bg-[rgb(var(--bg))] p-5 shadow-2xl"><div className="flex items-center justify-between"><div><h2 className="text-lg font-semibold">Permisos efectivos</h2><p className="text-sm text-[rgb(var(--muted))]">{effectivePermissions.length} permitidos · {catalog.length - effectivePermissions.length} sin acceso</p></div><button type="button" onClick={() => setShowEffective(false)} className="rounded-lg border px-3 py-2">Cerrar</button></div><input aria-label="Buscar permisos efectivos" value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Buscar..." className="my-4 w-full rounded-xl border px-3 py-2 text-sm"/><select aria-label="Módulo de permisos efectivos" value={effectiveModule} onChange={(event) => setEffectiveModule(event.target.value)} className="mb-3 w-full rounded-lg border bg-[rgba(var(--panel),0.95)] px-3 py-2 text-sm"><option value="all">Todos los módulos</option>{moduleNames.map((name) => <option key={name} value={name}>{name}</option>)}</select><div className="divide-y">{Object.entries(effectivePreviewGroups).map(([module, permissions]) => <section key={module}><h3 className="sticky top-0 bg-[rgb(var(--bg))] py-2 text-xs font-semibold uppercase text-[rgb(var(--muted))]">{module}</h3>{permissions.map((permission) => { const granted = form.permissionGrants.includes(permission.code); const denied = form.permissionDenies.includes(permission.code); const inherited = inheritedPermissions.some((item) => item.code === permission.code); const allowed = permissionPreview.effective.includes(permission.code); return <div key={permission.code} className="py-2"><p className="text-sm font-medium">{permission.name || permission.code}</p><p className="text-xs text-[rgb(var(--muted))]">{granted ? 'Concedido explícitamente' : denied ? 'Denegado explícitamente' : inherited ? 'Permitido por rol' : 'Sin permiso'} · {allowed ? 'Permitido' : 'No permitido'}</p></div>; })}</section>)}</div></div> : null}
        </section> : null}

        <StickyActions
          left={(
            <button type="button" onClick={() => { if (!hasUnsavedChanges || window.confirm('Hay cambios sin guardar. ¿Querés salir sin guardar?')) navigate('/seguridad/usuarios'); }} className="rounded-xl border border-[rgba(var(--border),0.65)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]">
              Volver
            </button>
          )}
          right={(
            <button type="submit" disabled={(Boolean(hierarchyWarning) && !hasLevelAtLeast(form.nivel)) || isSubmitting || (isEdit && !hasUnsavedChanges)} className="rounded-xl bg-[rgb(var(--brand))] px-5 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]">
              {isSubmitting ? 'Guardando...' : isEdit ? 'Guardar usuario' : 'Crear usuario'}
            </button>
          )}
        />
      </form>
    </div>
  );
}
